/**
 * FZ-4110 (T600) — ESQUELETO do serviço de dados da jornada.
 *
 * Responsabilidades deste arquivo:
 *  - único ponto de HTTP da jornada (GET protegido por `X-Journey-Token`);
 *  - estado tri-estado consumido pela UI (`state$`);
 *  - debounce de 1000 ms para os eventos de dado do realtime (`refetch()`);
 *  - leitura imediata com no máximo 1 pendência (`refreshNow()`);
 *  - reconciliação de erro (401 / 429 / 5xx / rede / timeout).
 *
 * T200/T301 evoluem estados visuais, cartões e acessibilidade — o "miolo" de
 * renderização NÃO fica aqui. Sem cache de payload (ADR-001): dedup somente
 * de requisições *in-flight*.
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, finalize, shareReplay, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';
import { JourneyResponseDto, PublicJourneyDto } from './models/journey.dto';

/** Estado macro da leitura da jornada (o T200 evolui a apresentação). */
export type JourneyStatus = 'idle' | 'loading' | 'available' | 'absent' | 'error';

/** Motivo do estado de erro. */
export type JourneyErrorReason = 'accessInvalid' | 'unavailable';

/** Estado publicado em `state$`. */
export interface JourneyState {
  status: JourneyStatus;
  reason?: JourneyErrorReason;
  /** Último dado bom; preservado em `error`/`unavailable` e em `loading`. */
  journey?: PublicJourneyDto;
  /** Backoff informado pelo backend no 429 (milissegundos). */
  retryAfterMs?: number;
}

@Injectable()
export class JourneyService {
  /** Teto para um GET pendurado (nunca deixa o in-flight preso). */
  static readonly REQUEST_TIMEOUT_MS = 10000;
  /** Janela do debounce dos eventos de dado do realtime. */
  static readonly REFETCH_WINDOW_MS = 1000;

  private readonly stateSubject = new BehaviorSubject<JourneyState>({ status: 'idle' });
  private readonly lastSyncSubject = new BehaviorSubject<Date | null>(null);

  /** Estado macro somente leitura para a UI. */
  readonly state$: Observable<JourneyState> = this.stateSubject.asObservable();
  /** Instante do último SUCESSO (não atualiza em erro). */
  readonly lastSyncAt$: Observable<Date | null> = this.lastSyncSubject.asObservable();

  /** Geração da sessão: incrementa em `reset()` e descarta respostas velhas. */
  private generation = 0;

  private accessKey = '';
  private credential = '';
  private configured = false;

  /** 401 encerra a sessão: nenhuma leitura automática até novo `configure`. */
  private accessBlocked = false;

  /** GET em voo por chave (somente dedup; nada de cache do payload). */
  private readonly inFlight = new Map<string, Observable<JourneyResponseDto>>();

  /** Timer do debounce de `refetch()`. */
  private refetchTimer: ReturnType<typeof setTimeout> | null = null;

  /** Leitura interna em andamento (controla a fila de 1 pendência). */
  private readInFlight = false;
  /** Já existe uma leitura agendada aguardando o GET atual terminar. */
  private pendingRefresh = false;

  /** Instante antes do qual não se deve disparar leitura (429). */
  private retryNotBefore = 0;

  constructor(private http: HttpClient) { }

  /**
   * Define a sessão pública que o serviço pode consultar.
   * Chamar com novas credenciais destrava um eventual 401 anterior.
   */
  configure(accessKey: string, credential: string): void {
    this.accessKey = accessKey || '';
    this.credential = credential || '';
    this.configured = !!(this.accessKey && this.credential);
    this.accessBlocked = false;
    this.retryNotBefore = 0;
  }

  /** Leitura inicial: publica `loading` e dispara o GET imediatamente. */
  load(): void {
    if (!this.canRead()) return;
    this.clearRefetchTimer();
    this.stateSubject.next({ status: 'loading', journey: this.stateSubject.value.journey });
    this.readNow();
  }

  /**
   * Evento de dado do realtime: agenda UMA leitura para +1000 ms.
   * Eventos que chegam com a janela já aberta NÃO reagendam.
   */
  refetch(): void {
    if (!this.canRead()) return;
    if (this.refetchTimer !== null) return;
    this.refetchTimer = setTimeout(() => {
      this.refetchTimer = null;
      this.readNow();
    }, JourneyService.REFETCH_WINDOW_MS);
  }

  /**
   * Reconciliação imediata (focus, visibility, reconexão).
   * Se houver GET em voo, compartilha essa leitura e guarda no máximo
   * UMA pendência para depois que ela terminar (nunca N pendências).
   */
  refreshNow(): void {
    if (!this.canRead()) return;
    this.clearRefetchTimer();
    this.readNow();
  }

  /**
   * Limpa estado e timers (401 e desmontagem). A geração é incrementada para
   * que uma resposta pendente não reaplique estado depois do reset.
   */
  reset(): void {
    this.generation++;
    this.clearRefetchTimer();
    this.readInFlight = false;
    this.pendingRefresh = false;
    this.retryNotBefore = 0;
    this.accessBlocked = false;
    this.inFlight.clear();
    this.stateSubject.next({ status: 'idle' });
    this.lastSyncSubject.next(null);
  }

  /**
   * HTTP cru da jornada (sem tocar no estado). Deduplica apenas *in-flight*:
   * assinantes simultâneos compartilham a mesma requisição; ao terminar
   * (sucesso ou erro), uma nova chamada dispara um novo GET.
   */
  get(accessKey: string, credential: string): Observable<JourneyResponseDto> {
    const key = this.keyOf(accessKey, credential);
    const existing = this.inFlight.get(key);
    if (existing) return existing;

    let stream: Observable<JourneyResponseDto>;
    stream = this.http
      .get<JourneyResponseDto>(`${environment.urlApi}v2/ticketing/public/tickets/${accessKey}/journey`, {
        headers: new HttpHeaders({ 'X-Journey-Token': credential })
      })
      .pipe(
        timeout(JourneyService.REQUEST_TIMEOUT_MS),
        finalize(() => {
          if (this.inFlight.get(key) === stream) this.inFlight.delete(key);
        }),
        shareReplay(1)
      );

    this.inFlight.set(key, stream);
    return stream;
  }

  /** Pode consultar? (configurado, sem 401 pendente e fora do backoff do 429) */
  private canRead(): boolean {
    if (!this.configured || this.accessBlocked) return false;
    return !this.isRetryBlocked();
  }

  private keyOf(accessKey: string, credential: string): string {
    return `${accessKey}:${credential}`;
  }

  private isRetryBlocked(): boolean {
    return Date.now() < this.retryNotBefore;
  }

  /** Ponto único de disparo da leitura interna, com fila de 1 pendência. */
  private readNow(): void {
    if (!this.canRead()) return;
    if (this.readInFlight) {
      this.pendingRefresh = true;
      return;
    }

    const generation = this.generation;
    const accessKey = this.accessKey;
    const credential = this.credential;
    const key = this.keyOf(accessKey, credential);

    this.readInFlight = true;
    const stream = this.get(accessKey, credential);
    stream
      .pipe(
        finalize(() => this.finishRead(generation, accessKey, key, stream))
      )
      .subscribe({
        next: (response) => {
          if (!this.isCurrent(generation, accessKey)) return;
          this.applyResponse(response);
        },
        error: (error) => {
          if (!this.isCurrent(generation, accessKey)) return;
          this.applyError(error);
        }
      });
  }

  private finishRead(
    generation: number,
    accessKey: string,
    key: string,
    stream: Observable<JourneyResponseDto>
  ): void {
    this.readInFlight = false;

    // O teardown dos operadores roda de dentro para fora: ESTE `finalize` (mais
    // interno) executa antes do `finalize` de `get()` que remove a entrada de
    // `inFlight`. Sem remover explicitamente aqui, a leitura pendente reusaria
    // um fluxo já concluído (shareReplay) e NÃO faria o GET seguinte.
    if (this.inFlight.get(key) === stream) this.inFlight.delete(key);

    if (!this.pendingRefresh) return;
    this.pendingRefresh = false;
    if (!this.isCurrent(generation, accessKey)) return;
    this.readNow();
  }

  private isCurrent(generation: number, accessKey: string): boolean {
    return this.generation === generation && this.accessKey === accessKey;
  }

  /** 200: `hasJourney` define available/absent; ambos atualizam `lastSyncAt$`. */
  private applyResponse(response: JourneyResponseDto): void {
    this.retryNotBefore = 0;
    this.lastSyncSubject.next(new Date());

    if (response && response.hasJourney && response.journey) {
      this.stateSubject.next({ status: 'available', journey: response.journey });
      return;
    }
    this.stateSubject.next({ status: 'absent' });
  }

  private applyError(error: HttpErrorResponse | any): void {
    const status = error?.status ?? 0;

    // 401: sessão inválida. Limpa a jornada, cancela timers e NÃO tenta de novo.
    if (status === 401) {
      this.accessBlocked = true;
      this.clearRefetchTimer();
      this.pendingRefresh = false;
      this.retryNotBefore = 0;
      this.stateSubject.next({ status: 'error', reason: 'accessInvalid' });
      return;
    }

    // 429: mantém o dado bom e respeita o `Retry-After` (em segundos).
    if (status === 429) {
      this.applyRateLimited(error);
      return;
    }

    // 5xx / rede / timeout: preserva a última jornada boa (nunca vira absent).
    const journey = this.stateSubject.value.journey;
    this.stateSubject.next({ status: 'error', reason: 'unavailable', journey });
  }

  private applyRateLimited(error: HttpErrorResponse | any): void {
    const retryAfterMs = this.parseRetryAfterMs(error);
    if (retryAfterMs !== null) {
      this.retryNotBefore = Date.now() + retryAfterMs;
    }

    const current = this.stateSubject.value;
    const journey = current.journey;
    const state: JourneyState = journey
      ? { status: 'available', journey }
      : current.status === 'absent'
        ? { status: 'absent' }
        : { status: 'error', reason: 'unavailable' };

    if (retryAfterMs !== null) state.retryAfterMs = retryAfterMs;
    this.stateSubject.next(state);
  }

  /** `Retry-After` é tratado apenas na forma de segundos (contrato do backend). */
  private parseRetryAfterMs(error: HttpErrorResponse | any): number | null {
    const raw = error?.headers?.get ? error.headers.get('Retry-After') : null;
    if (raw === null || raw === undefined || raw === '') return null;
    const seconds = parseInt(String(raw), 10);
    if (isNaN(seconds) || seconds < 0) return null;
    return seconds * 1000;
  }

  private clearRefetchTimer(): void {
    if (this.refetchTimer !== null) {
      clearTimeout(this.refetchTimer);
      this.refetchTimer = null;
    }
  }
}
